import { BaseAPI } from "./BaseApi";

export interface LfgListing {
  lfgId: string;
  gameId: string;
  title: string;
  description?: string;
  creatorId: string;
  creatorUsername: string;
  creatorAvatarUrl?: string;
  players: Array<{ userId: string; username: string; avatarUrl?: string; joinedAt: string }>;
  maxPlayers: number;
  status: "OPEN" | "FULL" | "MATCHED" | "CANCELLED" | "EXPIRED";
  preferences: {
    gameMode?: string;
    region?: string;
    skillLevel?: "casual" | "competitive";
    voiceRequired?: boolean;
    visibility: "public" | "private";
  };
  expiresAt: string;
  createdAt: string;
}

export interface BrowseListingView {
  lobbyId: string;
  title: string;
  description?: string;
  gameId: string;
  gameName: string;
  gameCoverUrl?: string;
  hostUserId: string;
  hostUsername: string;
  hostAvatarUrl?: string;
  memberCount: number;
  maxPlayers: number;
  preferences: {
    gameMode?: string;
    region?: string;
    skillLevel?: "casual" | "competitive";
    voiceRequired?: boolean;
  };
  createdAt: string;
}

export interface BrowseListingsParams {
  gameId?: string;
  gameMode?: string;
  region?: string;
  skillLevel?: string;
  sort?: "newest" | "closest_match" | "filling_soon";
  page?: number;
  pageSize?: number;
}

export interface BrowseListingsResponse {
  listings: BrowseListingView[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface CreateListingDto {
  gameId: string;
  title: string;
  maxPlayers: number;
  description?: string;
  preferences?: {
    gameMode?: string;
    region?: string;
    skillLevel?: "casual" | "competitive";
    voiceRequired?: boolean;
    visibility?: "public" | "private";
  };
}

export interface JoinQueueDto {
  gameId: string;
  gameMode: string;
  region?: string;
  skillLevel?: "casual" | "competitive";
  voiceRequired?: boolean;
  partySize: number;
}

export interface QueueEntry {
  queueEntryId: string;
  userId: string;
  gameId: string;
  preferences: {
    gameMode: string;
    region?: string;
    skillLevel: "casual" | "competitive";
    voiceRequired: boolean;
    partySize: number;
  };
  joinedAt: string;
  expiresAt: string;
}

export interface MatchRecord {
  matchId: string;
  gameId: string;
  lfgListingId: string;
  players: Array<{ userId: string; username: string; avatarUrl?: string; joinedAt: string }>;
  status: "ACTIVE" | "COMPLETED" | "CANCELLED";
  discordChannelId?: string | null;
  discordGuildId?: string | null;
  channelCreationFailed: boolean;
  createdAt: string;
}

export interface GameRating {
  rating: number;
  gamesPlayed: number;
  wins: number;
  losses: number;
  winRate: number;
}

export class LfgAPI extends BaseAPI {
  constructor() {
    super("");
  }

  async browseListings(params?: BrowseListingsParams): Promise<BrowseListingsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.gameId) searchParams.set("gameId", params.gameId);
    if (params?.gameMode) searchParams.set("gameMode", params.gameMode);
    if (params?.region) searchParams.set("region", params.region);
    if (params?.skillLevel) searchParams.set("skillLevel", params.skillLevel);
    if (params?.sort) searchParams.set("sort", params.sort);
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("pageSize", String(params.pageSize));

    const qs = searchParams.toString();
    const raw = await this.request<{ items: BrowseListingView[]; total: number; page: number; pageSize: number; hasMore: boolean }>(`/api/lfg${qs ? `?${qs}` : ""}`, {
      method: "GET",
      auth: true,
    });

    return {
      listings: raw.items,
      total: raw.total,
      page: raw.page,
      pageSize: raw.pageSize,
      hasMore: raw.hasMore,
    };
  }

  async createListing(data: CreateListingDto): Promise<LfgListing> {
    return this.request<LfgListing>("/api/lfg", {
      method: "POST",
      body: JSON.stringify(data),
      auth: true,
    });
  }

  async joinListing(lfgId: string): Promise<LfgListing> {
    return this.request<LfgListing>(`/api/lfg/${lfgId}/join`, {
      method: "POST",
      auth: true,
    });
  }

  async leaveListing(lfgId: string): Promise<LfgListing> {
    return this.request<LfgListing>(`/api/lfg/${lfgId}/leave`, {
      method: "POST",
      auth: true,
    });
  }

  async cancelListing(lfgId: string): Promise<LfgListing> {
    return this.request<LfgListing>(`/api/lfg/${lfgId}/cancel`, {
      method: "POST",
      auth: true,
    });
  }

  async joinQueue(data: JoinQueueDto): Promise<QueueEntry> {
    return this.request<QueueEntry>("/api/matchmaking/queue", {
      method: "POST",
      body: JSON.stringify(data),
      auth: true,
    });
  }

  async leaveQueue(): Promise<void> {
    return this.request<void>("/api/matchmaking/queue", {
      method: "DELETE",
      auth: true,
    });
  }

  async getRating(gameId: string): Promise<GameRating> {
    return this.request<GameRating>(`/api/matchmaking/rating/${gameId}`, {
      method: "GET",
      auth: true,
    });
  }

  async cancelMatch(matchId: string): Promise<MatchRecord> {
    return this.request<MatchRecord>(`/api/matches/${matchId}/cancel`, {
      method: "POST",
      auth: true,
    });
  }
}
